import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Popover,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';

const containerStyles = {
  position: 'absolute',
  top: 64,
  left: '50%',
  zIndex: 10,
  transform: 'translateX(-50%)',
} as const;
const toolbarStyles = {
  display: 'flex',
  alignItems: 'center',
  height: 34,
  px: 0.25,
  borderRadius: 1,
  overflow: 'hidden',
};
const arrowButtonStyles = { width: 30, height: 30, borderRadius: 0 };
const dateButtonStyles = {
  minWidth: 0,
  height: 30,
  px: 1.5,
  borderRadius: 0,
  color: 'text.primary',
  fontSize: 13,
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
const dateIconStyles = {
  ml: 0.5,
  fontSize: 16,
  transform: 'translateY(-1px)',
};
const cloudButtonStyles = {
  minWidth: 0,
  height: 30,
  px: 1.5,
  borderRadius: 0,
  gap: 0.75,
};
const cloudTrackStyles = {
  width: 24,
  height: 7,
  overflow: 'hidden',
  borderRadius: 4,
  bgcolor: 'action.disabledBackground',
  border: '1px solid',
  borderColor: 'text.primary',
};
const cloudIconStyles = {
  fontSize: 16,
  color: 'text.primary',
};
const cloudSliderContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  width: 210,
  gap: 1.25,
  px: 2,
  py: 1.5,
  '& .MuiSlider-valueLabel': { zIndex: 2 },
};
const cloudSliderStyles = { flex: 1 };
const cloudValueStyles = {
  minWidth: 34,
  textAlign: 'right',
  fontSize: 12,
  fontVariantNumeric: 'tabular-nums',
};
const cloudPopoverPaperProps = { sx: { overflow: 'visible' } };
const calendarStyles = { width: 310 };
const availableDayStyles = {
  bgcolor: 'primary.light',
  color: 'primary.contrastText',
  '&:hover': { bgcolor: 'primary.main' },
};
const popoverAnchorOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
} as const;
const popoverTransformOrigin = {
  vertical: 'top',
  horizontal: 'center',
} as const;

export interface CopernicusLayerSettingsProps {
  availableDates: string[];
  selectedDate: string | null;
  maxCloudCoverage: number;
  loading: boolean;
  canSelectPrevious: boolean;
  canSelectNext: boolean;
  onSelectDate: (date: string) => void;
  onSelectPrevious: () => void;
  onSelectNext: () => void;
  onCloudCoverageChange: (value: number) => void;
}

const formatDate = (date: string): string =>
  DateTime.fromISO(date, { zone: 'utc' }).toFormat('dd.MM.yyyy');

const CopernicusLayerSettings = React.memo(
  ({
    availableDates,
    selectedDate,
    maxCloudCoverage,
    loading,
    canSelectPrevious,
    canSelectNext,
    onSelectDate,
    onSelectPrevious,
    onSelectNext,
    onCloudCoverageChange,
  }: CopernicusLayerSettingsProps) => {
    const intl = useIntl();
    const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null);
    const [cloudAnchor, setCloudAnchor] = useState<HTMLElement | null>(null);
    const [draftCloudCoverage, setDraftCloudCoverage] = useState(
      maxCloudCoverage
    );

    useEffect(() => {
      setDraftCloudCoverage(maxCloudCoverage);
    }, [maxCloudCoverage]);

    const availableDateSet = useMemo(() => new Set(availableDates), [
      availableDates,
    ]);
    const selectedCalendarDate = selectedDate
      ? DateTime.fromISO(selectedDate, { zone: 'utc' })
      : null;
    const dateLabel = selectedDate
      ? formatDate(selectedDate)
      : loading
      ? intl.formatMessage({ id: 'loading', defaultMessage: 'Loading…' })
      : intl.formatMessage({
          id: 'map.copernicus.noImagery',
          defaultMessage: 'No imagery',
        });
    const cloudFillStyles = useMemo(
      () => ({
        width: `${draftCloudCoverage}%`,
        height: '100%',
        background:
          'linear-gradient(90deg, #90caf9 0%, #78909c 55%, #455a64 100%)',
      }),
      [draftCloudCoverage]
    );

    const openDatePicker = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setDateAnchor(event.currentTarget);
      },
      []
    );
    const closeDatePicker = useCallback(() => {
      setDateAnchor(null);
    }, []);
    const openCloudPicker = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setCloudAnchor(event.currentTarget);
      },
      []
    );
    const closeCloudPicker = useCallback(() => {
      setCloudAnchor(null);
    }, []);
    const shouldDisableDate = useCallback(
      (date: DateTime) => !availableDateSet.has(date.toISODate() || ''),
      [availableDateSet]
    );
    const getDaySlotProps = useCallback(
      ({ day, selected }: { day: DateTime; selected: boolean }) => ({
        sx:
          !selected && availableDateSet.has(day.toISODate() || '')
            ? availableDayStyles
            : undefined,
      }),
      [availableDateSet]
    );
    const selectDate = useCallback(
      (date: DateTime | null) => {
        const isoDate = date?.toISODate();
        if (isoDate && availableDateSet.has(isoDate)) {
          onSelectDate(isoDate);
          closeDatePicker();
        }
      },
      [availableDateSet, closeDatePicker, onSelectDate]
    );
    const changeDraftCloudCoverage = useCallback(
      (_event: Event, value: number | number[]) => {
        if (typeof value === 'number') {
          setDraftCloudCoverage(value);
        }
      },
      []
    );
    const commitCloudCoverage = useCallback(
      (_event: Event | React.SyntheticEvent, value: number | number[]) => {
        if (typeof value === 'number') {
          onCloudCoverageChange(value);
        }
      },
      [onCloudCoverageChange]
    );

    return (
      <Box sx={containerStyles}>
        <Paper elevation={4} sx={toolbarStyles}>
          <Tooltip
            title={intl.formatMessage({
              id: 'map.copernicus.previousDate',
              defaultMessage: 'Previous available date',
            })}
          >
            <span>
              <IconButton
                size="small"
                sx={arrowButtonStyles}
                disabled={!canSelectPrevious}
                onClick={onSelectPrevious}
                aria-label={intl.formatMessage({
                  id: 'map.copernicus.previousDateAria',
                  defaultMessage: 'Previous Copernicus date',
                })}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            color="inherit"
            sx={dateButtonStyles}
            onClick={openDatePicker}
            disabled={availableDates.length === 0}
            aria-label={intl.formatMessage({
              id: 'map.copernicus.selectDate',
              defaultMessage: 'Select Copernicus date',
            })}
          >
            {dateLabel}
            <CalendarMonthOutlinedIcon sx={dateIconStyles} />
          </Button>
          <Tooltip
            title={intl.formatMessage(
              {
                id: 'map.copernicus.maximumCloudCoverValue',
                defaultMessage: 'Maximum cloud cover: {value}%',
              },
              { value: maxCloudCoverage }
            )}
          >
            <Button
              color="inherit"
              sx={cloudButtonStyles}
              onClick={openCloudPicker}
              aria-label={intl.formatMessage(
                {
                  id: 'map.copernicus.maximumCloudCoverValue',
                  defaultMessage: 'Maximum cloud cover: {value}%',
                },
                { value: maxCloudCoverage }
              )}
            >
              <Box sx={cloudTrackStyles}>
                <Box sx={cloudFillStyles} />
              </Box>
              <CloudOutlinedIcon sx={cloudIconStyles} />
            </Button>
          </Tooltip>
          <Tooltip
            title={intl.formatMessage({
              id: 'map.copernicus.nextDate',
              defaultMessage: 'Next available date',
            })}
          >
            <span>
              <IconButton
                size="small"
                sx={arrowButtonStyles}
                disabled={!canSelectNext}
                onClick={onSelectNext}
                aria-label={intl.formatMessage({
                  id: 'map.copernicus.nextDateAria',
                  defaultMessage: 'Next Copernicus date',
                })}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Paper>

        <Popover
          open={Boolean(dateAnchor)}
          anchorEl={dateAnchor}
          onClose={closeDatePicker}
          anchorOrigin={popoverAnchorOrigin}
          transformOrigin={popoverTransformOrigin}
        >
          <DateCalendar
            value={selectedCalendarDate}
            onChange={selectDate}
            shouldDisableDate={shouldDisableDate}
            slotProps={{ day: getDaySlotProps }}
            sx={calendarStyles}
          />
        </Popover>

        <Popover
          open={Boolean(cloudAnchor)}
          anchorEl={cloudAnchor}
          onClose={closeCloudPicker}
          anchorOrigin={popoverAnchorOrigin}
          transformOrigin={popoverTransformOrigin}
          PaperProps={cloudPopoverPaperProps}
        >
          <Box sx={cloudSliderContainerStyles}>
            <Slider
              min={0}
              max={100}
              step={5}
              value={draftCloudCoverage}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}%`}
              onChange={changeDraftCloudCoverage}
              onChangeCommitted={commitCloudCoverage}
              aria-label={intl.formatMessage({
                id: 'map.copernicus.maximumCloudCover',
                defaultMessage: 'Maximum cloud cover',
              })}
              sx={cloudSliderStyles}
            />
            <Typography sx={cloudValueStyles}>{draftCloudCoverage}%</Typography>
          </Box>
        </Popover>
      </Box>
    );
  }
);

export { CopernicusLayerSettings };
